import React, { useMemo, useRef, useState, useEffect } from "react";
import { Select } from "antd";
import { isEqual } from "lodash";
import debounce from "lodash/debounce";
import NotFoundContent from "@/components/NotFoundContent";
const { Option } = Select;
export default function SelectAutoComplete({ fetchOptions, parseFetchedOptions, debounceTimeout = 600, isLoading, options, renderOption, placeholder, value, onChange, defaultActiveFirstOption, style, disabled = false, customFilter, autoPopulateOptions = false, mode, }) {
    const [fetching, setFetching] = useState(false);
    const [currentOptions, setCurrentOptions] = useState(() => options ?? []);
    const fetchRef = useRef(0);
    useEffect(() => {
        if (options?.length > 0 && !isEqual(options, currentOptions)) {
            const newOptions = customFilter ? customFilter(options) : options;
            setCurrentOptions(newOptions);
        }
    }, [options, customFilter, currentOptions]);
    const debounceFetcher = useMemo(() => {
        const loadOptions = (fetchOptionsParams) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setCurrentOptions([]);
            setFetching(true);
            fetchOptions(fetchOptionsParams)
                .then(response => (typeof parseFetchedOptions === "function" ? parseFetchedOptions(response) : response))
                .then(newOptions => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }
                setCurrentOptions(newOptions?.items ?? []);
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [debounceTimeout, fetchOptions, parseFetchedOptions]);
    useEffect(() => {
        if (autoPopulateOptions) {
            debounceFetcher("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- debounceFetcher is overriden each render; leave out of dependencies to avoid fetching every render
    }, [autoPopulateOptions]);
    const currentValue = useMemo(() => {
        if (mode === "multiple") {
            return Array.isArray(value) ? value.filter(Boolean) : [];
        }
        return value;
    }, [value, mode]);
    return (React.createElement(Select, { value: currentValue, onChange: onChange, placeholder: placeholder, filterOption: false, onSearch: debounceFetcher, notFoundContent: NotFoundContent({ loading: fetching || isLoading }), loading: fetching || isLoading, style: style, defaultActiveFirstOption: defaultActiveFirstOption, disabled: disabled, onClear: () => {
            onChange?.(mode === "multiple" ? [] : null); // clear to [] for multi, null for single
            fetchOptions("");
        }, allowClear: true, showSearch: true, mode: mode }, currentOptions?.map(option => renderOption ? (renderOption(option) // custom render
    ) : (React.createElement(Option, { key: option.id, value: option.value }, option.label)))));
}
SelectAutoComplete.defaultProps = {
    debounceTimeout: 600,
    isLoading: false,
    renderOption: undefined,
    placeholder: undefined,
    value: undefined,
    onChange: undefined,
    defaultActiveFirstOption: false,
    style: undefined,
    disabled: false,
    options: undefined,
    customFilter: undefined,
    autoPopulateOptions: false,
    parseFetchedOptions: undefined,
};
